/**
 * Subheader (ES-13522) — the new generic page subheader.
 *
 * A single dark bar shared by every page type: title (string or an injected
 * control), an optional tab row, and an opt-in actions cluster (favorite,
 * feedback, share, print). It owns NO page-specific logic — vehicle specifics
 * reach it only through generic slots (`title` node, `onMobileNavOpen`).
 *
 * This is the scaffold: structure + tabs + navigation + actions wired to the
 * Phase 0 hooks/services. Scroll dock->pill behavior (1b), the full feedback
 * popover/toast polish (1e), and skeletons (1g) build on top of this.
 *
 * @param {string|React.ReactNode} title  String, or a control (e.g. the VP year dropdown).
 * @param {string} baseUrl                 Base path for default tab navigation.
 * @param {Array|null} tabs                Tab[] ({ id, urlPath, subcategory, externalUrl?, badge?, printable? }); null/empty => no tab row.
 * @param {object|null} currentTab         The active tab.
 * @param {function} [onNavigate]          (urlPath) => void; default = router push to `${baseUrl}/${urlPath}`.
 * @param {function} [hrefFor]             (urlPath) => the path onNavigate opens, so hovering a tab can prefetch it.
 * @param {object} [actions]               { favorite?, feedback?, share?, print? } opt-in booleans; print may be 'auto'.
 * @param {function} [onMobileNavOpen]     Mobile page/section dropdown pass-through (VP opens its nav overlay).
 * @param {boolean} [mobileNavOpen]        Whether that pass-through's overlay is open (flips the picker chevron).
 * @param {string} [pageKey]               Storage key for favorite/feedback; defaults to the current pathname.
 * @param {Array} [customActions]          [{ id, label, Icon, onClick }] trailing icon buttons (onClick owns analytics); tablet/desktop only.
 * @param {boolean} [ownsBack]             false for the automatic back bar so it doesn't hide itself.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { useIsMobile } from "../../utils/hooks/useIsMobile";
import AppBackButton from "../../components/molecules/AppBack/AppBackButton";
import { useBackDismiss } from "../../components/contexts/backNavigationContext";
import { useSubheaderPrint } from "../../components/contexts/subheaderPrintContext";
import * as subheaderAnalytics from "./subheader.analytics.js";
import { useFavorite } from "./useFavorite.js";
import { COMMENT_MAX_LENGTH, usePageFeedback } from "./usePageFeedback.js";
import { useSubheaderScroll } from "./useSubheaderScroll.js";
import {
  ChevronDownIcon,
  ExternalLinkIcon,
  HeartIcon,
  OverflowIcon,
  PrintIcon,
  ShareIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "./subheaderIcons.js";
import styles from "./Subheader.module.scss";
import ShareContent from "./ShareContent.js";

const MOBILE_BREAKPOINT = 800;
// Below this width the action icons (favorite/feedback/share/print) collapse
// into the ⋯ overflow. Kept lower than MOBILE_BREAKPOINT so tablets still show
// every icon inline — only true phones fall back to the overflow menu.
const ACTION_OVERFLOW_BREAKPOINT = 600;
// Desktop tab sets at/above this collapse to the active-tab overflow dropdown.
const TAB_OVERFLOW_THRESHOLD = 6;
// Fallbacks for --sh-pill-side-desktop / --sh-pill-gap-desktop.
const PILL_SIDE_FALLBACK = 24;
const PILL_GAP_FALLBACK = 28;
// Min space between the inline tabs and the actions.
const TAB_FIT_SLACK = 8;
// Minimum space kept between the mobile picker menu and the viewport edges.
const PICKER_GUTTER = 8;
// Selected-vote fills from the mockup: up = blue, down = red (both ~85% alpha),
// with a white stroke on the dark bar.
const VOTE_UP_FILL = "#3BB1E9DA";
const VOTE_DOWN_FILL = "#EB0A1EDA";
// On white surfaces (mobile overflow menu) the stroke can't be white — use a
// slightly-darker blue/red outline instead, matching the search-page thumbs.
const VOTE_UP_OUTLINE = "#2F9CCD";
const VOTE_DOWN_OUTLINE = "#CF0A1A";

// Tooltip suppression: after a click, hide the button's data-tip bubble (it
// shouldn't linger over the button the pointer is still resting on) until the
// pointer leaves and returns, or the button is re-focused.
const holdTip = (e) => e.currentTarget.setAttribute("data-tip-suppressed", "");
const releaseTip = (e) =>
  e.currentTarget.removeAttribute("data-tip-suppressed");

// Pages can briefly mount two Subheaders; the last one out clears the marker.
let mountedSubheaders = 0;

const Subheader = ({
  title,
  baseUrl = "",
  tabs,
  currentTab,
  onNavigate,
  hrefFor,
  actions = {},
  analytics = {},
  onMobileNavOpen,
  mobileNavOpen = false,
  pageKey,
  loading = false,
  favoriteTitle: vehicleFavoriteTitle,
  customActions = [],
  ownsBack = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  // Separate, narrower breakpoint for the action icons so tablets keep them
  // inline (only phones collapse into the ⋯ overflow).
  const isActionOverflow = useIsMobile(ACTION_OVERFLOW_BREAKPOINT);

  const titleString = typeof title === "string" ? title : "";
  const titleContent = titleString ? (
    <span className={styles.titleText}>{titleString}</span>
  ) : (
    title
  );
  const url = pageKey || location.pathname;

  // Stable ref so action handlers fire the latest analytics context without
  // being re-created on every render.
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const showTabs = hasTabs;

  // --- Favorite -------------------------------------------------------------
  // Enrich the saved favorite so the nav panel can title + breadcrumb it
  // meaningfully (these persist in the bookmark's title/subcategory columns and
  // the panel prefers them over URL-derived guesses).
  //   • Vehicle pages — title = the sub-section's nav label (`favoriteTitle`),
  //     else left to the URL; subcategory = the real "{Year} {Model}" so the
  //     breadcrumb reads e.g. "2026 Corolla Cross · Details".
  //   • Other pages — title = the current tab (e.g. "Resources", "Overview"),
  //     subcategory = the page it belongs to (e.g. "SmartPath", "Beyond Zero"),
  //     so a "Resources" tab on SmartPath vs EV is no longer indistinguishable.
  const analyticsData = analytics || {};
  const isVehiclePage = Boolean(
    analyticsData.modelName && analyticsData.modelYear,
  );
  let favoriteTitle = "";
  let favoriteSubcategory = "";
  if (isVehiclePage) {
    favoriteTitle = vehicleFavoriteTitle || "";
    favoriteSubcategory = `${analyticsData.modelYear} ${analyticsData.modelName}`;
  } else {
    const currentTabLabel =
      typeof currentTab?.id === "string" ? currentTab.id : "";
    const pageName = analyticsData.pageTitle || titleString;
    favoriteTitle = hasTabs && currentTabLabel ? currentTabLabel : pageName;
    favoriteSubcategory = hasTabs && currentTabLabel ? pageName : "";
  }

  const favorite = useFavorite(
    actions.favorite
      ? {
          url,
          title: favoriteTitle,
          subcategory: favoriteSubcategory,
        }
      : {},
  );

  // --- Feedback -------------------------------------------------------------
  // Vote, comment box and "thanks" come from the hook shared with the article
  // header; this component only adds the celebration animation and closes the
  // share menu on a vote.
  const {
    vote,
    ready: feedbackReady,
    comment,
    setComment,
    commentOpen,
    setCommentOpen,
    thanks,
    prompt: commentPrompt,
    castVote: castFeedbackVote,
    sendComment,
    skipComment,
  } = usePageFeedback({
    url,
    enabled: Boolean(actions.feedback),
    analytics: {
      feedbackClick: (resolved) =>
        subheaderAnalytics.feedbackClick(analyticsRef.current, resolved),
      feedbackCommentSend: () =>
        subheaderAnalytics.feedbackCommentSend(analyticsRef.current),
      feedbackCommentSkip: () =>
        subheaderAnalytics.feedbackCommentSkip(analyticsRef.current),
    },
  });
  // Per-action animation ticks — bumping one replays that button's celebration
  // (keying the icon wrapper remounts it so the CSS animation runs fresh).
  const [anim, setAnim] = useState({ fav: 0, up: 0, down: 0 });
  const animTimers = useRef({});
  // Bump a tick to play the celebration, then reset it to 0 once the animation
  // has finished. The reset matters for buttons that live in a menu that
  // unmounts/remounts (the mobile ⋯ overflow): without it the tick would stay
  // > 0 and the CSS animation would replay every time the menu is reopened,
  // even without a tap.
  const triggerAnim = useCallback((kind) => {
    setAnim((a) => ({ ...a, [kind]: a[kind] + 1 }));
    clearTimeout(animTimers.current[kind]);
    animTimers.current[kind] = setTimeout(() => {
      setAnim((a) => ({ ...a, [kind]: 0 }));
    }, 600);
  }, []);
  useEffect(() => {
    const timers = animTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const castVote = useCallback(
    (nextVote) => {
      // Re-tapping the active vote clears it (handled in the hook).
      const resolved = castFeedbackVote(nextVote);
      setOpenMenu(null); // close the share popover (mutually exclusive)
      if (resolved) triggerAnim(resolved);
    },
    [castFeedbackVote, triggerAnim],
  );

  // --- Share ----------------------------------------------------------------
  // One compact popover for both breakpoints (openMenu === "share"); anchored
  // under the actions cluster. onShare is defined lower down (needs toggleMenu).
  const shareLink = typeof window !== "undefined" ? window.location.href : url;

  // --- Print ----------------------------------------------------------------
  // A page with a dedicated print layout registers its own print handler via
  // the SubheaderPrint context; when one is present the Print button shows and
  // delegates to it (falling back to a plain window.print()).
  const { printHandler } = useSubheaderPrint();
  const onPrint = useCallback(() => {
    subheaderAnalytics.printClick(analyticsRef.current);
    if (printHandler) printHandler();
    else window.print();
  }, [printHandler]);

  // --- Tab navigation -------------------------------------------------------
  const tabHref = (tab) => {
    if (tab.externalUrl) {
      return undefined;
    }
    if (onNavigate) {
      return hrefFor?.(tab.urlPath);
    }
    return `${baseUrl}/${tab.urlPath}`;
  };
  const goToTab = useCallback(
    (tab) => {
      if (!tab) return;
      subheaderAnalytics.tabClick(analyticsRef.current, tab.id);
      if (tab.externalUrl) {
        window.open(tab.externalUrl, "_blank", "noopener,noreferrer");
        return;
      }
      if (onNavigate) onNavigate(tab.urlPath);
      else navigate(`${baseUrl}/${tab.urlPath}`);
    },
    [onNavigate, navigate, baseUrl],
  );

  // A single open-menu token gives the one-open-at-a-time invariant for free
  // (picker / tabs overflow / actions overflow). The comment popover is tracked
  // separately (it's tied to a vote).
  const [openMenu, setOpenMenu] = useState(null); // 'picker' | 'tabs' | 'overflow' | null
  const pickerOpen = openMenu === "picker";
  const tabsMenuOpen = openMenu === "tabs";
  const overflowOpen = openMenu === "overflow";
  const shareMenuOpen = openMenu === "share";
  const toggleMenu = useCallback(
    (name) => setOpenMenu((cur) => (cur === name ? null : name)),
    [],
  );
  const closeMenus = useCallback(() => {
    setOpenMenu(null);
    setCommentOpen(false);
  }, []);

  // Share: one popover for both breakpoints. Opening it closes the feedback
  // comment box (and vice-versa, handled in castVote) — they're mutually
  // exclusive so they never overlap.
  const onShare = useCallback(() => {
    setCommentOpen(false);
    if (openMenu !== "share")
      subheaderAnalytics.shareClick(analyticsRef.current);
    toggleMenu("share");
  }, [toggleMenu, openMenu]);

  // Mobile section picker: opens the VP nav overlay when a pass-through is
  // provided, otherwise the built-in inline tab dropdown.
  const onPickerClick = useCallback(() => {
    if (onMobileNavOpen) onMobileNavOpen();
    else toggleMenu("picker");
  }, [onMobileNavOpen, toggleMenu]);

  // The picker menu anchors to the picker's left edge, but the picker sits
  // mid-bar (after the title), so a long legacy tab list can run off the right
  // of a phone. Measure on open and shift it left to stay inside the viewport.
  // offsetWidth (not getBoundingClientRect) so the entrance scale is ignored.
  const pickerRef = useRef(null);
  const pickerMenuRef = useRef(null);
  const [pickerShift, setPickerShift] = useState(0);
  useLayoutEffect(() => {
    if (!pickerOpen) {
      setPickerShift(0);
      return undefined;
    }
    const measure = () => {
      const picker = pickerRef.current;
      const menu = pickerMenuRef.current;
      if (!picker || !menu) return;
      const anchorLeft = picker.getBoundingClientRect().left;
      const overflowRight =
        anchorLeft + menu.offsetWidth - (window.innerWidth - PICKER_GUTTER);
      const minShift = PICKER_GUTTER - anchorLeft;
      setPickerShift(Math.max(Math.min(0, -overflowRight), minShift));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [pickerOpen]);

  // Tab fit check, measured against the pill (the narrower state) so the pill
  // and docked bar always collapse together.
  const headerRef = useRef(null);
  const backRef = useRef(null);
  const titleRef = useRef(null);
  const actionsRef = useRef(null);
  const tabMeasureRef = useRef(null);
  const [tabsFit, setTabsFit] = useState(true);
  const measureTabs =
    !loading && !isMobile && hasTabs && tabs.length < TAB_OVERFLOW_THRESHOLD;
  useLayoutEffect(() => {
    if (!measureTabs) {
      setTabsFit(true);
      return undefined;
    }
    const measure = () => {
      const bar = headerRef.current;
      const row = tabMeasureRef.current;
      if (!bar || !row) return;
      const barStyle = window.getComputedStyle(bar);
      const px = (v) => parseFloat(v) || 0;
      const container =
        bar.offsetWidth + px(barStyle.marginLeft) + px(barStyle.marginRight);
      // Not laid out yet.
      if (!container) return;
      const rootStyle = window.getComputedStyle(document.documentElement);
      const pillSide =
        px(rootStyle.getPropertyValue("--sh-pill-side-desktop")) ||
        PILL_SIDE_FALLBACK;
      const pillGap =
        px(rootStyle.getPropertyValue("--sh-pill-gap-desktop")) ||
        PILL_GAP_FALLBACK;
      const pillInner =
        container -
        2 * pillSide -
        px(barStyle.borderLeftWidth) -
        px(barStyle.borderRightWidth) -
        px(barStyle.paddingLeft) -
        px(barStyle.paddingRight);
      // The app back button is one more flex item: its box (net of negative margins) plus a gap.
      const back = backRef.current;
      const backStyle = back ? window.getComputedStyle(back) : null;
      const backWidth = back
        ? back.offsetWidth +
          px(backStyle.marginLeft) +
          px(backStyle.marginRight) +
          pillGap
        : 0;
      const needed =
        backWidth +
        (titleRef.current?.offsetWidth || 0) +
        (actionsRef.current?.offsetWidth || 0) +
        row.offsetWidth +
        2 * pillGap +
        TAB_FIT_SLACK;
      setTabsFit(needed <= pillInner);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    [
      headerRef.current,
      backRef.current,
      titleRef.current,
      actionsRef.current,
      tabMeasureRef.current,
    ]
      .filter(Boolean)
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [measureTabs]);

  // Desktop overflow: long or non-fitting tab sets collapse to a dropdown.
  const collapsed =
    !isMobile && hasTabs && (tabs.length >= TAB_OVERFLOW_THRESHOLD || !tabsFit);

  // Mobile actions overflow (⋯): favorite + feedback + share (+ print) move off
  // the bar into the popup.
  const hasOverflowActions =
    actions.favorite || actions.feedback || actions.share || actions.print;

  // Desktop/tablet-only group dividers so the row reads as three distinct
  // purposes: favorite (save) | feedback thumbs (rate) | share/print (utility).
  const dividerBeforeFeedback =
    !isActionOverflow && actions.favorite && actions.feedback;
  const dividerBeforeUtility =
    !isActionOverflow && actions.feedback && (actions.share || actions.print);

  const activeLabel = currentTab?.id ?? tabs?.[0]?.id ?? "";

  // Scroll state machine (dock -> hide -> glass pill). Stay visible while a
  // menu/popover is open.
  const menuOpen = openMenu !== null || commentOpen;
  const scrollState = useSubheaderScroll({ locked: menuOpen });
  // Android back closes an open menu/popover before leaving the page.
  useBackDismiss(closeMenus, menuOpen);

  // Close the open menu/popover on Escape or an outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMenus();
    };
    const onPointer = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        closeMenus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [menuOpen, closeMenus]);

  // Measure the real sticky main-nav height (64px) so
  // the fixed bar docks flush beneath it on every breakpoint — the static token
  // is only a fallback. Also publishes the measurement + a marker class on the
  // root element so page-owned sticky content can offset itself below the pill
  // (see --sh-content-top in _subheaderTokens.scss).
  const [navHeight, setNavHeight] = useState(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    mountedSubheaders += 1;
    root.classList.add("has-unified-subheader");
    const measure = () => {
      const nav = document.querySelector(".topnav");
      const h = nav ? Math.round(nav.getBoundingClientRect().height) : null;
      setNavHeight(h);
      if (h != null) root.style.setProperty("--sh-main-nav", `${h}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      mountedSubheaders -= 1;
      if (mountedSubheaders === 0) {
        root.classList.remove("has-unified-subheader");
        root.style.removeProperty("--sh-main-nav");
      }
    };
  }, []);

  // Mirror the pill's hidden state on the root so page sticky content
  // (EV / SmartPath tabs) hides and reveals with it (see usePillFollower).
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("sh-pill-hidden", scrollState === "hidden");
    return () => root.classList.remove("sh-pill-hidden");
  }, [scrollState]);

  const actionButtons = useMemo(
    () => ({
      favorite: actions.favorite,
      feedback: actions.feedback,
      share: actions.share,
      // Print shows when a page explicitly opts in OR registers a print handler.
      print: actions.print || !!printHandler,
    }),
    [
      actions.favorite,
      actions.feedback,
      actions.share,
      actions.print,
      printHandler,
    ],
  );

  // Feedback comment popover — anchored under the thumbs on desktop, or
  // under the ⋯ overflow on mobile. Only one branch renders per breakpoint, so
  // this single node is never mounted twice.
  const commentPopoverNode = (
    <div className={styles.commentPopover} role="dialog" aria-label="Feedback">
      <div className={styles.commentTitle}>{commentPrompt.title}</div>
      <div className={styles.commentSub}>
        Optional — your vote is already saved.
      </div>
      <textarea
        className={styles.commentInput}
        placeholder={commentPrompt.placeholder}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={COMMENT_MAX_LENGTH}
        rows={3}
      />
      <div className={styles.commentActions}>
        <button
          type="button"
          className={styles.commentDismiss}
          onClick={skipComment}
        >
          Skip
        </button>
        <button
          type="button"
          className={styles.commentSend}
          onClick={sendComment}
        >
          Send feedback
        </button>
      </div>
    </div>
  );

  return (
    <header
      ref={headerRef}
      className={[
        "unified-subheader-bar",
        styles.dock,
        scrollState === "pinned" ? styles.pinned : styles.pill,
        scrollState === "hidden" ? styles.hidden : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        navHeight != null ? { "--sh-main-nav": `${navHeight}px` } : undefined
      }
      data-testid="subheader"
      data-scroll-state={scrollState}
    >
      <AppBackButton
        ref={backRef}
        variant="bar"
        analytics={analytics}
        ownsBack={ownsBack}
      />

      {/* Title (region A) */}
      <div
        ref={titleRef}
        className={styles.title}
        onClickCapture={() => setOpenMenu(null)}
      >
        {loading ? (
          <span
            className={styles.skeleton}
            style={{ width: 120 }}
            aria-hidden="true"
          />
        ) : (
          titleContent
        )}
      </div>

      {/* Hidden tab row copy for the fit check */}
      {measureTabs && (
        <ul
          ref={tabMeasureRef}
          className={`${styles.tabRow} ${styles.tabMeasure}`}
          aria-hidden="true"
        >
          {tabs.map((tab) => (
            <li key={tab.id}>
              <span className={styles.tab}>
                <span>
                  {tab.id}
                  {tab.externalUrl && (
                    <ExternalLinkIcon size={8} className={styles.tabExt} />
                  )}
                </span>
                {tab.badge != null && (
                  <span className={styles.badge}>{tab.badge}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Tabs (region B) */}
      {loading && (
        <div className={styles.nav} aria-hidden="true">
          <div className={styles.skelTabs}>
            <span className={styles.skeleton} style={{ width: 70 }} />
            <span className={styles.skeleton} style={{ width: 90 }} />
            <span className={styles.skeleton} style={{ width: 64 }} />
          </div>
        </div>
      )}
      {!loading && showTabs && (
        <nav
          className={[styles.nav, collapsed ? styles.navStart : ""]
            .filter(Boolean)
            .join(" ")}
          aria-label="Section navigation"
        >
          {/* Desktop: inline tab row, or an active-tab overflow dropdown when long */}
          {!isMobile && !collapsed && (
            <ul className={styles.tabRow}>
              {tabs.map((tab) => {
                const isActive = currentTab?.urlPath === tab.urlPath;
                return (
                  <li key={tab.id}>
                    <button
                      type="button"
                      className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => goToTab(tab)}
                      data-prefetch-href={tabHref(tab)}
                    >
                      <span>
                        {tab.id}
                        {tab.externalUrl && (
                          <ExternalLinkIcon
                            size={8}
                            className={styles.tabExt}
                          />
                        )}
                      </span>
                      {tab.badge != null && (
                        <span className={styles.badge}>{tab.badge}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Desktop overflow: single active-tab trigger + dropdown + count badge */}
          {!isMobile && collapsed && (
            <div className={styles.overflow}>
              <button
                type="button"
                className={styles.overflowTrigger}
                aria-haspopup="menu"
                aria-expanded={tabsMenuOpen}
                onClick={() => {
                  if (openMenu !== "tabs")
                    subheaderAnalytics.tabDropdownClick(analyticsRef.current);
                  toggleMenu("tabs");
                }}
              >
                <span className={`${styles.tab} ${styles.tabActive}`}>
                  {activeLabel}
                </span>
                <span className={styles.countBadge}>· {tabs.length}</span>
                <ChevronDownIcon size={16} />
              </button>
              {tabsMenuOpen && (
                <ul className={styles.menu} role="menu">
                  {tabs.map((tab) => {
                    const isActive = currentTab?.urlPath === tab.urlPath;
                    return (
                      <li key={tab.id} role="none">
                        <button
                          type="button"
                          role="menuitem"
                          className={`${styles.menuItem} ${isActive ? styles.menuItemActive : ""}`}
                          aria-current={isActive ? "page" : undefined}
                          onClick={() => {
                            setOpenMenu(null);
                            goToTab(tab);
                          }}
                        >
                          <span>{tab.id}</span>
                          {tab.externalUrl && (
                            <span className={styles.extLine}>
                              <ExternalLinkIcon
                                size={8}
                                className={styles.menuItemExt}
                              />
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Mobile: inline section picker (opens overlay via pass-through or a dropdown) */}
          {isMobile && (
            <div className={styles.picker} ref={pickerRef}>
              <button
                type="button"
                className={styles.pickerButton}
                aria-haspopup="menu"
                aria-expanded={onMobileNavOpen ? mobileNavOpen : pickerOpen}
                onClick={onPickerClick}
              >
                <span>{activeLabel}</span>
                <ChevronDownIcon size={14} />
              </button>
              {pickerOpen && !onMobileNavOpen && (
                <ul
                  ref={pickerMenuRef}
                  className={styles.pickerMenu}
                  role="menu"
                  style={pickerShift ? { left: pickerShift } : undefined}
                >
                  {tabs.map((tab) => (
                    <li key={tab.id} role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.pickerItem}
                        onClick={() => {
                          setOpenMenu(null);
                          goToTab(tab);
                        }}
                      >
                        {tab.id}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </nav>
      )}

      {/* Actions (region C) */}
      <div ref={actionsRef} className={styles.actions}>
        {actionButtons.favorite && !isActionOverflow && (
          <button
            type="button"
            className={styles.actionBtn}
            aria-pressed={favorite.isFavorite}
            aria-label={`Favorite ${titleString}`.trim()}
            data-tip={favorite.isFavorite ? "Unfavorite" : "Favorite"}
            onMouseLeave={releaseTip}
            onBlur={releaseTip}
            onClick={(e) => {
              holdTip(e);
              if (!favorite.isFavorite) {
                triggerAnim("fav");
              }
              subheaderAnalytics.favoriteClick(
                analyticsRef.current,
                !favorite.isFavorite,
              );
              favorite.toggle();
            }}
          >
            <span
              key={`fav-${anim.fav}`}
              className={`${styles.iconAnim} ${anim.fav > 0 ? styles.heartAnim : ""}`}
            >
              {anim.fav > 0 && (
                <span className={styles.burstHeart} aria-hidden="true" />
              )}
              <HeartIcon
                size={19}
                filled={favorite.isFavorite}
                style={
                  favorite.isFavorite
                    ? { color: "var(--sh-favorite-fill)" }
                    : undefined
                }
              />
            </span>
          </button>
        )}

        {dividerBeforeFeedback && (
          <span className={styles.actionDivider} aria-hidden="true" />
        )}

        {actionButtons.feedback && !isActionOverflow && (
          <>
            <button
              type="button"
              className={styles.actionBtn}
              aria-pressed={vote === "up"}
              aria-label="Helpful"
              data-tip="This page was helpful"
              disabled={!feedbackReady}
              onMouseLeave={releaseTip}
              onBlur={releaseTip}
              onClick={(e) => {
                holdTip(e);
                castVote("up");
              }}
            >
              <span
                key={`up-${anim.up}`}
                className={`${styles.iconAnim} ${anim.up > 0 ? styles.upAnim : ""}`}
              >
                {anim.up > 0 && (
                  <span className={styles.burstUp} aria-hidden="true" />
                )}
                <ThumbsUpIcon
                  size={19}
                  filled={vote === "up"}
                  fillColor={VOTE_UP_FILL}
                  style={vote === "up" ? { color: "#FFFFFF" } : undefined}
                />
              </span>
            </button>
            <div className={styles.feedbackAnchor}>
              <button
                type="button"
                className={styles.actionBtn}
                aria-pressed={vote === "down"}
                aria-label="Not helpful"
                data-tip="This page could be better"
                disabled={!feedbackReady}
                onMouseLeave={releaseTip}
                onBlur={releaseTip}
                onClick={(e) => {
                  holdTip(e);
                  castVote("down");
                }}
              >
                <span
                  key={`down-${anim.down}`}
                  className={`${styles.iconAnim} ${anim.down > 0 ? styles.downAnim : ""}`}
                >
                  {anim.down > 0 && (
                    <span className={styles.burstDown} aria-hidden="true" />
                  )}
                  <ThumbsDownIcon
                    size={19}
                    filled={vote === "down"}
                    fillColor={VOTE_DOWN_FILL}
                    style={vote === "down" ? { color: "#FFFFFF" } : undefined}
                  />
                </span>
              </button>
              {commentOpen && commentPopoverNode}
            </div>
          </>
        )}

        {dividerBeforeUtility && (
          <span className={styles.actionDivider} aria-hidden="true" />
        )}

        {actionButtons.share && !isActionOverflow && (
          <button
            type="button"
            className={styles.actionBtn}
            aria-label="Share"
            aria-haspopup="dialog"
            aria-expanded={shareMenuOpen}
            data-tip="Share"
            onMouseLeave={releaseTip}
            onBlur={releaseTip}
            onClick={(e) => {
              holdTip(e);
              onShare();
            }}
          >
            <ShareIcon size={19} />
          </button>
        )}

        {actionButtons.print && !isActionOverflow && (
          <button
            type="button"
            className={styles.actionBtn}
            aria-label="Print"
            data-tip="Print this page"
            onMouseLeave={releaseTip}
            onBlur={releaseTip}
            onClick={(e) => {
              holdTip(e);
              onPrint();
            }}
          >
            <PrintIcon size={19} />
          </button>
        )}

        {!isActionOverflow &&
          customActions.length > 0 &&
          hasOverflowActions && (
            <span className={styles.actionDivider} aria-hidden="true" />
          )}

        {!isActionOverflow &&
          customActions.map(({ id, label, Icon, onClick }) => (
            <button
              key={id}
              type="button"
              className={styles.actionBtn}
              aria-label={label}
              data-tip={label}
              onMouseLeave={releaseTip}
              onBlur={releaseTip}
              onClick={(e) => {
                holdTip(e);
                onClick();
              }}
            >
              <Icon size={19} />
            </button>
          ))}

        {/* Phones: favorite + feedback + share + print collapse into a ⋯ menu */}
        {isActionOverflow && hasOverflowActions && (
          <div className={styles.overflow}>
            <button
              type="button"
              className={styles.actionBtn}
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
              aria-label="More actions"
              onClick={() => {
                setCommentOpen(false); // tapping ⋯ dismisses the feedback box
                toggleMenu("overflow");
              }}
            >
              <OverflowIcon size={20} />
            </button>
            {overflowOpen && (
              <ul className={styles.overflowMenu} role="menu">
                {actionButtons.favorite && (
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.overflowItem}
                      aria-pressed={favorite.isFavorite}
                      onClick={() => {
                        if (!favorite.isFavorite) {
                          triggerAnim("fav");
                        }
                        subheaderAnalytics.favoriteClick(
                          analyticsRef.current,
                          !favorite.isFavorite,
                        );
                        favorite.toggle();
                      }}
                    >
                      <span
                        key={`ovf-fav-${anim.fav}`}
                        className={`${styles.iconAnim} ${anim.fav > 0 ? styles.heartAnim : ""}`}
                      >
                        {anim.fav > 0 && (
                          <span
                            className={styles.burstHeart}
                            aria-hidden="true"
                          />
                        )}
                        <HeartIcon
                          size={18}
                          filled={favorite.isFavorite}
                          style={
                            favorite.isFavorite
                              ? { color: "var(--sh-favorite-fill)" }
                              : undefined
                          }
                        />
                      </span>
                      <span>
                        {favorite.isFavorite ? "Unfavorite" : "Favorite"}
                      </span>
                    </button>
                  </li>
                )}
                {actionButtons.favorite && actionButtons.feedback && (
                  <li className={styles.overflowDivider} role="separator" />
                )}
                {actionButtons.feedback && (
                  <>
                    <li role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.overflowItem}
                        disabled={!feedbackReady}
                        onClick={() => {
                          setOpenMenu(null);
                          castVote("up");
                        }}
                      >
                        <ThumbsUpIcon
                          size={18}
                          filled={vote === "up"}
                          fillColor={VOTE_UP_FILL}
                          style={
                            vote === "up"
                              ? { color: VOTE_UP_OUTLINE }
                              : undefined
                          }
                        />
                        <span>Helpful</span>
                      </button>
                    </li>
                    <li role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.overflowItem}
                        disabled={!feedbackReady}
                        onClick={() => {
                          setOpenMenu(null);
                          castVote("down");
                        }}
                      >
                        <ThumbsDownIcon
                          size={18}
                          filled={vote === "down"}
                          fillColor={VOTE_DOWN_FILL}
                          style={
                            vote === "down"
                              ? { color: VOTE_DOWN_OUTLINE }
                              : undefined
                          }
                        />
                        <span>Not helpful</span>
                      </button>
                    </li>
                  </>
                )}
                {(actionButtons.favorite || actionButtons.feedback) &&
                  (actionButtons.share || actionButtons.print) && (
                    <li className={styles.overflowDivider} role="separator" />
                  )}
                {actionButtons.share && (
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.overflowItem}
                      onClick={onShare}
                    >
                      <ShareIcon size={18} />
                      <span>Share</span>
                    </button>
                  </li>
                )}
                {actionButtons.print && (
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.overflowItem}
                      onClick={() => {
                        setOpenMenu(null);
                        onPrint();
                      }}
                    >
                      <PrintIcon size={18} />
                      <span>Print</span>
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}

        {thanks && (
          <span className={styles.toast} role="status">
            Thanks for your feedback!
          </span>
        )}

        {shareMenuOpen && (
          <div className={styles.sharePopover} role="dialog" aria-label="Share">
            <ShareContent
              link={shareLink}
              onOptionClick={(option) =>
                subheaderAnalytics.shareOptionClick(
                  analyticsRef.current,
                  option,
                )
              }
            />
          </div>
        )}

        {isActionOverflow && commentOpen && commentPopoverNode}
      </div>
    </header>
  );
};

export default Subheader;
